import {
  Activity,
  ArrowUpRight,
  Building2,
  CarFront,
  CircleDollarSign,
  Gauge,
  KeyRound,
  LogOut,
  Menu,
  Send,
  Settings2,
  ShieldCheck,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "./auth";
import { statusLabel } from "./presentation";

export { activityLabel, entityLabel, fiscalIntegerFromDisplay, integrationLastCheckLabel, integrationSummary, roleLabel, statusLabel } from "./presentation";

export function formatCurrency(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  const absoluteCents = Math.abs(cents);
  const euros = Math.floor(absoluteCents / 100);
  const decimalCents = absoluteCents % 100;
  const localeInteger = new Intl.NumberFormat("es-ES", {
    maximumFractionDigits: 0,
    useGrouping: true,
  }).format(euros);
  // Intl deja los importes de cuatro cifras sin agrupar en es-ES. En una UI
  // financiera resulta más legible mantener el millar de forma sistemática.
  const groupedInteger = euros >= 1_000 && euros < 10_000 && !localeInteger.includes(".")
    ? `${String(euros).slice(0, 1)}.${String(euros).slice(1)}`
    : localeInteger;
  const signedInteger = cents < 0 ? `-${groupedInteger}` : groupedInteger;
  return decimalCents === 0
    ? `${signedInteger} €`
    : `${signedInteger},${String(decimalCents).padStart(2, "0")} €`;
}

export function formatDate(value: string | null | undefined, includeTime = false): string {
  if (!value) return "Sin datos";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin datos";
  return new Intl.DateTimeFormat("es-ES", includeTime
    ? { dateStyle: "medium", timeStyle: "short" }
    : { dateStyle: "medium" }).format(date);
}

export function formatCount(count: number, singular: string, plural = `${singular}s`): string {
  return `${count.toLocaleString("es-ES")} ${count === 1 ? singular : plural}`;
}

export function moveTabFocus(event: ReactKeyboardEvent<HTMLButtonElement>) {
  const tabs = Array.from(event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []);
  const currentIndex = tabs.indexOf(event.currentTarget);
  let nextIndex: number | null = null;
  if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (currentIndex + 1) % tabs.length;
  if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
  if (event.key === "Home") nextIndex = 0;
  if (event.key === "End") nextIndex = tabs.length - 1;
  if (nextIndex === null) return;
  const nextTab = tabs[nextIndex];
  if (!nextTab) return;
  event.preventDefault();
  nextTab.focus();
  nextTab.click();
}

const primaryNavigation = [
  { to: "/", label: "Visión general", icon: Gauge, end: true },
  { to: "/concesionarios", label: "Concesionarios", icon: Building2 },
  { to: "/vehiculos", label: "Vehículos", icon: CarFront },
  { to: "/negocio", label: "Negocio y referencias", icon: CircleDollarSign },
  { to: "/equipo-cochegest", label: "Equipo Cochegest", icon: ShieldCheck },
];

const advancedNavigation = [
  { to: "/automatizaciones", label: "Automatizaciones", icon: Settings2 },
  { to: "/eventos", label: "Eventos de dominio", icon: Send },
  { to: "/salud", label: "Diagnóstico técnico", icon: Activity },
];

export function PlatformLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const tenantAppUrl = import.meta.env.VITE_TENANT_APP_URL ?? "http://127.0.0.1:5173";

  useEffect(() => {
    setMobileOpen(false);
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname]);

  return <div className="platform-shell">
    <a className="skip-link" href="#platform-main">Saltar al contenido</a>
    <button className="mobile-menu" aria-label={mobileOpen ? "Cerrar navegación" : "Abrir navegación"} aria-expanded={mobileOpen} onClick={() => setMobileOpen((value) => !value)}>{mobileOpen ? <X /> : <Menu />}</button>
    <aside className={mobileOpen ? "platform-sidebar is-open" : "platform-sidebar"}>
      <div className="platform-brand" aria-label="Cochegest"><img src="/cochegest-higgsfield-ui.webp?v=15892f18-ui-256" width={256} height={256} alt="" aria-hidden="true" decoding="async" /></div>
      <div className="environment-pill" title="Entorno de producción"><i /> Plataforma activa</div>
      <nav aria-label="Administración de plataforma">
        <span className="platform-nav-label">Gestión</span>
        {user?.mfaEnabled ? primaryNavigation.map(({ to, label, icon: Icon, end }) => <NavLink to={to} end={end} key={to} className={({ isActive }) => isActive ? "active" : ""}><Icon /><span>{label}</span></NavLink>) : <NavLink to="/seguridad" className="active"><KeyRound /><span>Proteger mi cuenta</span></NavLink>}
        {user?.mfaEnabled && <details className="platform-nav-advanced" open={advancedNavigation.some((item) => location.pathname.startsWith(item.to)) || undefined}>
          <summary>Herramientas avanzadas</summary>
          <div>
            {advancedNavigation.map(({ to, label, icon: Icon }) => <NavLink to={to} key={to} className={({ isActive }) => isActive ? "active" : ""}><Icon /><span>{label}</span></NavLink>)}
          </div>
        </details>}
      </nav>
      <div className="platform-sidebar__footer">
        {user?.mfaEnabled && <a href={tenantAppUrl} target="_blank" rel="noreferrer"><ArrowUpRight /><span>Abrir app de concesionario</span></a>}
        <NavLink to="/seguridad" className={({ isActive }) => isActive ? "active" : ""}><KeyRound /><span>Seguridad de mi cuenta</span></NavLink>
        <div className="platform-user"><span>{user?.name.slice(0, 2).toUpperCase()}</span><div><strong>{user?.name}</strong><small>{user?.email}</small></div></div>
        <button onClick={() => void logout()}><LogOut />Cerrar sesión</button>
      </div>
    </aside>
    <main id="platform-main" className="platform-main" ref={mainRef} tabIndex={-1}>{children}</main>
  </div>;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow: string; title: string; description: string; actions?: ReactNode }) {
  return <header className="platform-page-header"><div><p>{eyebrow}</p><h1>{title}</h1><span>{description}</span></div>{actions && <div className="header-actions">{actions}</div>}</header>;
}

export function LoadingState({ label = "Cargando datos globales…" }: { label?: string }) {
  return <div className="loading-state" role="status"><span /><strong>{label}</strong><small>Estamos consolidando la información de la plataforma.</small></div>;
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  return <div className="error-state" role="alert"><ShieldCheck /><div><strong>No se ha podido cargar esta vista</strong><p>{error instanceof Error ? error.message : "Se ha producido un error inesperado."}</p>{retry && <button className="button button-secondary" onClick={retry}>Reintentar</button>}</div></div>;
}

export function EmptyState({ title, detail, icon: Icon = Building2 }: { title: string; detail: string; icon?: LucideIcon }) {
  return <div className="empty-state" role="status"><Icon /><strong>{title}</strong><p>{detail}</p></div>;
}

export function StatusBadge({ value }: { value: string | null | undefined }) {
  const normalized = value ?? "unknown";
  return <span className={`status status-${normalized.replaceAll("_", "-")}`}><i />{statusLabel(normalized)}</span>;
}

export function TechnicalDetails({ items, summary = "Datos técnicos" }: {
  items: Array<{ label: string; value: string | number | null | undefined }>;
  summary?: string;
}) {
  const visibleItems = items.filter((item) => item.value !== null && item.value !== undefined && String(item.value).trim().length > 0);
  if (!visibleItems.length) return null;
  return <details className="technical-details"><summary>{summary}</summary><dl>{visibleItems.map((item) => <div key={`${item.label}-${item.value}`}><dt>{item.label}</dt><dd><code>{item.value}</code></dd></div>)}</dl></details>;
}

export function Panel({ title, eyebrow, children, action, className = "" }: { title: string; eyebrow?: string; children: ReactNode; action?: ReactNode; className?: string }) {
  return <section className={`panel ${className}`}><header><div>{eyebrow && <p>{eyebrow}</p>}<h2>{title}</h2></div>{action}</header>{children}</section>;
}

export function Toast({ message, tone = "success", onClose }: { message: string; tone?: "success" | "error"; onClose(): void }) {
  useEffect(() => { const timer = window.setTimeout(onClose, 4200); return () => window.clearTimeout(timer); }, [onClose]);
  return <div className={`platform-toast ${tone}`} role="status"><span>{message}</span><button aria-label="Cerrar" onClick={onClose}><X /></button></div>;
}
